import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import ConfirmDialog from '../common/ConfirmDialog';

interface EmailDomainMapping {
  id: number;
  domain: string;
  organization_id: number;
  created_by: string | null;
  created_at: string;
}

interface OrganizationEmailDomainsTabProps {
  organizationId: number;
}

export default function OrganizationEmailDomainsTab({ organizationId }: OrganizationEmailDomainsTabProps) {
  const [domains, setDomains] = useState<EmailDomainMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<EmailDomainMapping | null>(null);

  // Add dialog state
  const [domainInput, setDomainInput] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    loadDomains();
  }, [organizationId]);

  const loadDomains = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<EmailDomainMapping[]>(
        `/admin/organizations/${organizationId}/email-domains`
      );
      setDomains(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email domains');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setAddDialogOpen(true);
    setDomainInput('');
    setAddError(null);
  };

  const handleAddDomain = async () => {
    if (!domainInput.trim()) {
      setAddError('Domain is required');
      return;
    }

    try {
      setAddLoading(true);
      setAddError(null);
      await apiClient.post(`/admin/organizations/${organizationId}/email-domains`, {
        domain: domainInput.trim(),
      });
      setAddDialogOpen(false);
      await loadDomains();
    } catch (err: unknown) {
      setAddError(extractErrorMessage(err, 'Failed to add email domain'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveDomain = (domain: EmailDomainMapping) => {
    setSelectedDomain(domain);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedDomain) return;

    try {
      await apiClient.delete(
        `/admin/organizations/${organizationId}/email-domains/${selectedDomain.id}`
      );
      setRemoveDialogOpen(false);
      setSelectedDomain(null);
      await loadDomains();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove email domain');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Email Domain Mappings</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} size="small">
          Add Domain
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Domain</TableCell>
              <TableCell>Date Added</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains.map((domain) => (
              <TableRow key={domain.id}>
                <TableCell>{domain.domain}</TableCell>
                <TableCell>{formatDate(domain.created_at)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveDomain(domain)}
                    title="Remove domain"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {domains.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No email domains yet
          </Typography>
        </Box>
      )}

      {/* Add Domain Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Email Domain</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {addError && (
              <Alert severity="error" onClose={() => setAddError(null)}>
                {addError}
              </Alert>
            )}
            <TextField
              label="Domain"
              placeholder="e.g. example.com"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddDomain}
            variant="contained"
            disabled={addLoading || !domainInput.trim()}
          >
            {addLoading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <ConfirmDialog
        open={removeDialogOpen}
        title="Remove Email Domain"
        message={`Are you sure you want to remove the domain "${selectedDomain?.domain}"? This action cannot be undone.`}
        confirmText="Remove"
        confirmColor="error"
        onConfirm={handleConfirmRemove}
        onCancel={() => {
          setRemoveDialogOpen(false);
          setSelectedDomain(null);
        }}
      />
    </Box>
  );
}
